import { apiClient } from '@shared/api/client';

export interface NotificationItem {
  id: string;
  type: 'assignment' | 'negative-feedback';
  source: string;
  title: string;
  body: string;
  route: string;
  createdAt: string;
  readAt: string | null;
  deliveries: Array<{
    channel: string;
    status: string;
    attempts: number;
    errorCode: string | null;
  }>;
}
export interface NotificationPage {
  items: NotificationItem[];
  page: number;
  pageSize: number;
  total: number;
  unreadCount: number;
}
export interface NotificationPreferences {
  emailEnabled: boolean;
  whatsAppEnabled: boolean;
  desktopEnabled: boolean;
  assignmentsEnabled: boolean;
  negativeFeedbackEnabled: boolean;
  whatsAppPhone: string;
  email: string;
  version: string;
  systemEmailEnabled: boolean;
  systemWhatsAppEnabled: boolean;
  systemDesktopEnabled: boolean;
}
export interface NotificationRule {
  source: string;
  enabled: boolean;
  headUserIds: string[];
  negativeDefinition: string;
}
export interface NotificationMapping {
  id: string;
  source: string;
  externalIdentity: string;
  userId: string;
}
export interface NotificationSettings {
  enabled: boolean;
  emailEnabled: boolean;
  whatsAppEnabled: boolean;
  desktopEnabled: boolean;
  whatsAppProfileId: number;
  assignmentIntervalSeconds: number;
  feedbackIntervalSeconds: number;
  feedbackPagesPerCycle: number;
  portalBaseUrl: string;
  startedAt: string;
  version: string;
  rules: NotificationRule[];
  mappings: NotificationMapping[];
  recipients: Array<{ id: string; name: string; email: string }>;
  sources: Array<{
    source: string;
    lastRunAt: string | null;
    lastSuccessAt: string | null;
    errorCode: string | null;
    sweepPending: boolean;
    lastItemCount: number;
  }>;
  pendingDeliveries: number;
  failedDeliveries: number;
  unmatchedFeedbackCount: number;
}
export const notificationLink = (id: string) =>
  `/notifications?notification=${encodeURIComponent(id)}`;
export const safeNotificationRoute = (route: string) =>
  /^\/(tickets(?:\/|\?|$)|inbox\/(?:whatsapp|live-chat)(?:\/|\?|$)|customers-feedback\/(?:live-chat|recovery-tracking|ea-customer-survey)(?:\?|$))/.test(
    route,
  )
    ? route
    : '/notifications';
export const notificationApi = {
  list: async (page: number, unreadOnly: boolean, signal: AbortSignal) =>
    (
      await apiClient.get<NotificationPage>('/notifications', {
        params: { page, unreadOnly },
        signal,
      })
    ).data,
  detail: async (id: string, signal: AbortSignal) =>
    (await apiClient.get<NotificationItem>(`/notifications/${encodeURIComponent(id)}`, { signal }))
      .data,
  read: async (id?: string, read = true) => {
    await apiClient.post(
      `/notifications/${id ? `${encodeURIComponent(id)}/${read ? 'read' : 'unread'}` : 'read-all'}`,
    );
  },
  preferences: async (signal: AbortSignal) =>
    (await apiClient.get<NotificationPreferences>('/notification-preferences', { signal })).data,
  savePreferences: async (data: NotificationPreferences) =>
    (await apiClient.put<NotificationPreferences>('/notification-preferences', data)).data,
  settings: async (signal: AbortSignal) =>
    (await apiClient.get<NotificationSettings>('/notification-settings', { signal })).data,
  saveSettings: async (data: NotificationSettings) =>
    (await apiClient.put<NotificationSettings>('/notification-settings', data)).data,
  issues: async (signal: AbortSignal) =>
    (
      await apiClient.get<
        Array<{ source: string; externalIdentity: string; reference: string; seenAt: string }>
      >('/notification-settings/routing-issues', { signal })
    ).data,
};
